import { Injectable } from '@angular/core';
import {doc, Firestore, getDoc, getFirestore, onSnapshot, setDoc, updateDoc} from 'firebase/firestore';
import { InsertionPoint, TodoItemNode } from '../../model/notes.model';
import { LocalDatabaseService } from './local-database.service';
import { AuthService } from '../authentication/auth.service';

@Injectable({
  providedIn: 'root'
})

/**
 * Service responsible for managing the insertion point functionality.
 */
export class InsertionPointService {

  /**
   * The local database service used by the InsertionPointService.
   */
  private localDb: LocalDatabaseService;

  /**
   * The current insertion point location.
   */
  insertionPointLocation: InsertionPoint = null;

  private firestore: Firestore = getFirestore();

  constructor(private authService: AuthService) {
  }

  /**
   * Sets the local database for the InsertionPointService.
   * @param localDb The ChecklistDatabaseService instance to set as the local database.
   */
  setLocalDb(localDb: LocalDatabaseService) {
    this.localDb = localDb;
  }

    /**
     * Saves the current location in the database.
     * @param node The current node representing the location.
     */
    saveCurrentLocation(node: TodoItemNode) {
      // Referência ao documento no Firestore
      const docRef = doc(
        this.firestore,
        `users/${this.authService.userId}/settings/insertion_point`
      );

      const parent_id = node.id;

      // Armazena a nota selecionada no banco de dados local
      this.localDb.selectedNote = node;

      // Monta as strings de localização
      const locationString = this.assemblyLocationString(node);
      const compactedLocationString = this.limitText(locationString, 50);

      const ipToSave: InsertionPoint = {
        notebook_id: node.notebook_id,
        parent_id: parent_id,
        priority: node.maxChildrenPriorityNumber,
        location_string: locationString,
        compacted_location_string: compactedLocationString,
        has_child: node.has_child ? node.has_child : false,
      };

      // Salva o ponto de inserção no Firestore
      return setDoc(docRef, ipToSave).then(() => {
        console.log('Insertion point saved successfully');
      }).catch((error) => {
        console.error('Error saving insertion point:', error);
      });

      // Reordenação lógica dos dados no banco de dados local (exemplo comentado)
      /*
      let index = this.localDb.data.findIndex(itemNode => { return itemNode.id == node.notebook_id });
      let rootNode = this.localDb.data[index];

      this.localDb.data.splice(index, 1);
      this.localDb.data.unshift(rootNode);
      this.localDb.dataChange.next(this.localDb.data);
      */
    }

  /**
   * Assembles the location string by traversing the parent references of the given node.
   * @param node The current node representing the location.
   * @returns The assembled location string (ex. notebook1 > note1 > note12).
   */
  assemblyLocationString(node: TodoItemNode) {
    let locationString: string;

    locationString = node.item;
    let currentNode = node.parentRef;
    while (currentNode) {
      locationString = currentNode.item + ' > ' + locationString;
      currentNode = currentNode.parentRef;
    }

    return locationString;
  }

  /**
   * Limits the length of a text by truncating it and adding ellipsis if necessary.
   * @param text The text to be limited.
   * @param maxLength The maximum length of the text.
   * @returns The truncated text with ellipsis if necessary.
   */
  limitText(text: string, maxLength: number): string {

    if (!text || text.length <= maxLength) {
      return text;
    }

    const blocks = text.split(" > ");
    const numBlocks = blocks.length;
    const blockMaxLength = Math.floor(maxLength / numBlocks);

    let result = "";
    let currentLength = 0;

    for (let i = 0; i < numBlocks; i++) {
      const block = blocks[i];
      const blockLength = block.length;
      const truncatedBlock = block.slice(0, Math.min(blockLength, blockMaxLength));

      if (truncatedBlock.length + 3 <= blockLength) {
        result += truncatedBlock.trim() + ".."; // Add ellipsis if the block is truncated
      } else {
        result += truncatedBlock.trim();
      }

      result += " > ";
      currentLength += truncatedBlock.length + 3; // Adding 3 for "> " separator

      if (currentLength >= maxLength) {
        break;
      }
    }

    return result.slice(0, -3); // Remove the last "> " separator
  }

  // Não adianta - node estão fechado - tenho q fazer de outra forma --
  // dar um get reverso no DB
               //mas nao tem haver com isso - salva string location na hora de salvar no banco - vai ter dai certinho
  /**
   * Recursively searches for a child node with the specified ID within the parent node.
   * @param parentNode The parent node to search within.
   * @param id The ID of the child node to search for.
   * @returns The found child node, or null if not found.
   */
  searchChildrenNodeById(parentNode: TodoItemNode, id: string): TodoItemNode {
    let foundNode = null;

    if (!parentNode.children)
      return null;

    const foundIndex = parentNode.children.findIndex(itemNode => { return itemNode.id == id });

    if (foundIndex < 0) {
      // If the child node is not found in the current level, recursively search in the children of each child node
      for (let index = 0; index < parentNode.children.length; index++) {
        let result = this.searchChildrenNodeById(parentNode.children[index], id);
        if (result)
          return result;
      }

      return null; // Child node not found
    }

    return parentNode.children[foundIndex]; // Return the found child node
  }

  /**
   * Retrieves the insertion point node based on the current insertion point location.
   * @returns The insertion point node, or null if not found.
   */
  getInsertionPointNode(): TodoItemNode {

    if (!this.insertionPointLocation)
      return null;

    const foundIndex = this.localDb.data.findIndex(itemNode => { return itemNode.id == this.insertionPointLocation.notebook_id });

    if (foundIndex < 0) {
      // Notebook not found
      return null;
    }

    const notebook = this.localDb.data[foundIndex];

    // Check if location is the notebook itself
    let nodeLocation;

    if (this.insertionPointLocation.parent_id == notebook.id)
      nodeLocation = notebook;
    else
      nodeLocation = this.searchChildrenNodeById(notebook, this.insertionPointLocation.parent_id);

    return nodeLocation;
  }

/**  NOT USED
 * Retrieves the insertion point from the Firestore database.
 * @returns The insertion point node, or null if not found.
 */
async getInsertionPoint() {
  try {
    // Referência ao documento no Firestore
    const docRef = doc(
      this.firestore,
      `users/${this.authService.userId}/settings/insertion_point`
    );

    // Recupera o documento
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const location: InsertionPoint = docSnap.data() as InsertionPoint;
      location.priority = location.priority + 100;

      // Encontra o nó correspondente no banco de dados local
      const foundIndex = this.localDb.data.findIndex(
        (itemNode) => itemNode.id == location.notebook_id
      );

      if (foundIndex < 0) {
        // Notebook não encontrado
        return;
      }

      const notebook = this.localDb.data[foundIndex];

      // Verifica se a localização é o próprio notebook
      let nodeLocation;

      if (location.parent_id == notebook.id) {
        nodeLocation = notebook;
      } else {
        nodeLocation = this.searchChildrenNodeById(notebook, location.parent_id);
      }

      if (nodeLocation) {
        // Nó encontrado, monta a string de localização
        this.assemblyLocationString(nodeLocation);
      }
    }
  } catch (error) {
    console.error('Error retrieving insertion point:', error);
  }
}

  /**
   * Starts the listener for the insertion point in the Firestore database.
   * This function listens for changes in the insertion point document and updates the local database accordingly.
   */
  startInsertionPointListener() {
    // Referência ao documento no Firestore
    const docRef = doc(
      this.firestore,
      `users/${this.authService.userId}/settings/insertion_point`
    );

    // Inicia o listener para alterações no documento
    const unsubscribe = onSnapshot(
      docRef,
      { includeMetadataChanges: true }, // Inclui alterações nos metadados
      (doc) => {
        // Verifica se o documento tem alterações pendentes
        if (!doc.metadata.hasPendingWrites) {
          const location = doc.data() as InsertionPoint;

          // Atualiza o banco de dados local com a localização
          this.localDb.insertionPointString = location.location_string;
          this.localDb.selectedInsertionPointChange.next(
            this.localDb.insertionPointString
          );
          this.insertionPointLocation = location;
        }
      },
      (error) => {
        console.error('Error listening to insertion point:', error);
      }
    );

    // Armazene a função de cancelamento (unsubscribe) se necessário
    return unsubscribe;
  }

  updateInsertionPointInDb(insertionPoint: Partial<InsertionPoint>) {
    // Referência ao documento no Firestore
    const docRef = doc(
      this.firestore,
      `users/${this.authService.userId}/settings/insertion_point`
    );

    // Atualiza o documento no Firestore
    return updateDoc(docRef, insertionPoint)
      .then(() => {
        console.log('Insertion point updated successfully');
      })
      .catch((error) => {
        console.error('Error updating insertion point:', error);
      });
  }


// Bug  - se selecionar caderno com IP e nao tiver carregado os notes, não sabe a prioridade
// -- por isso tem que manter a prioridade

}
